/**
 * apiClient.js
 *
 * Axios instance with:
 * - Base URL and timeout configuration
 * - Request interceptors for auth token and cache busting
 * - Response interceptors for global error handling
 * - Automatic token refresh
 * - Global loading progress bar integration
 */

import axios from "axios";
import { startLoading, stopLoading } from "../components/loading/ProgressBar";
import { redirectToLogin, isCredentialCheck } from "../utils/authRedirect";

// ----------------------
// Config
// ----------------------
const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";
const API_TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT) || 30000;

// Create axios instance
// withCredentials ensures httpOnly auth cookies are sent on every request.
// Tokens are no longer stored in or read from localStorage — they live in
// httpOnly cookies that JavaScript cannot access, eliminating XSS token theft.
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// ----------------------
// Request Interceptor
// ----------------------
apiClient.interceptors.request.use(
  (config) => {
    startLoading();

    // Cache-busting for GET requests
    if (config.method === "get") {
      config.params = { ...config.params, _t: Date.now() };
    }

    return config;
  },
  (error) => {
    stopLoading();
    return Promise.reject(error);
  }
);

// ----------------------
// Response Interceptor
// ----------------------
// ── Runaway guards ───────────────────────────────────────────────────────
// Production incident (Sept 2026): an app-wide provider fetched an auth-gated
// endpoint anonymously on the login portal. Each 401 triggered a refresh, the
// refresh 401'd, and the handler hard-redirected to /login — which reloaded the
// page and started again. Tenant portals reload-looped forever. Both guards
// below exist so a single mistake like that degrades instead of spinning.

// Guard 1 — single-flight refresh. Without this, N requests failing at once
// (a dashboard fires half a dozen in parallel) produce N simultaneous
// refresh-token POSTs: a self-inflicted stampede on the auth endpoint at
// exactly the moment the session is already unhealthy.
let refreshInFlight = null;
const refreshSession = () => {
  if (!refreshInFlight) {
    refreshInFlight = axios
      .post(`${API_BASE_URL}/auth/refresh-token`, {}, { withCredentials: true })
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
};

// Guard 2 — redirect-loop breaker, in utils/authRedirect so the legacy
// services/api.js client shares the SAME counter. Two independent breakers
// would each stay under their own limit while a loop alternating between the
// two clients reloaded the page forever.

apiClient.interceptors.response.use(
  (response) => {
    stopLoading();
    return response; // We leave it as response, services will handle `.data`
  },
  async (error) => {
    stopLoading();

    const originalRequest = error.config;

    // A 401 from these means "the credentials/code you submitted are wrong",
    // NOT "your existing session expired mid-request" — attempting a session
    // refresh (which also 401s, since there's no session) and then hard-
    // redirecting to /login on a login/MFA attempt just reloads the page,
    // wiping the form with no error shown. Let their 401 fall through to the
    // normal error-message handling below instead.
    const credentialCheck = isCredentialCheck(originalRequest?.url);

    // ----------------------
    // Auto Token Refresh
    // ----------------------
    // The refreshToken lives in an httpOnly cookie — no need to read or send it
    // manually. The browser includes it automatically in the refresh request.
    if (error.response?.status === 401 && !originalRequest._retry && !credentialCheck) {
      originalRequest._retry = true;

      try {
        // Shared promise: concurrent 401s wait on ONE refresh, not one each.
        await refreshSession();
        // New accessToken cookie has been set by the server — retry original request
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed — clear non-sensitive local state and redirect
        localStorage.removeItem("user");
        localStorage.removeItem("university");
        localStorage.removeItem("needsBilling");
        localStorage.removeItem("selectedPlan");

        redirectToLogin();

        return Promise.reject(refreshError);
      }
    }

    // ----------------------
    // Per-plan upgrade gate (HTTP 402) — surface a targeted upsell instead of a
    // raw error. A global listener (UpgradeGateListener) shows the dialog.
    // ----------------------
    if (error.response?.status === 402 && error.response?.data?.code === "PLAN_UPGRADE_REQUIRED") {
      try {
        window.dispatchEvent(new CustomEvent("plan:upgrade-required", { detail: error.response.data }));
      } catch { /* non-browser env */ }
      return Promise.reject(new Error(error.response.data.message || "Upgrade your plan to use this feature."));
    }

    // ----------------------
    // Default: reject with a normalized, user-friendly message
    // ----------------------
    let normalizedError;
    if (error.response?.data?.message) {
      // The server already sent a friendly message — use it verbatim.
      normalizedError = error.response.data.message;
    } else if (error.response?.status >= 500) {
      normalizedError = "Something went wrong on our end. Please try again in a moment.";
    } else if (error.code === "ECONNABORTED" || /timeout/i.test(error.message || "")) {
      normalizedError = "The server is taking too long to respond. Please try again.";
    } else if (!error.response) {
      // No response at all — almost always a connectivity problem.
      normalizedError = "Unable to reach the server. Please check your internet connection and try again.";
    } else {
      normalizedError = "Something went wrong. Please try again.";
    }

    // ⚠️ Carry the original response through. Rejecting with a BARE `new Error`
    // silently broke every caller written the idiomatic axios way: a dozen pages
    // read `err.response.data.message`, which is `undefined` on a plain Error, so
    // they all fell through to their own generic fallback and the server's
    // message was never shown. That is how a deliberately-worded refusal — the
    // avatar 403 that explains WHY profile pictures are limited, written so the
    // refusal would not read as a bug — reached the user as "we couldn't update
    // your photo, please try again". Nothing errored; the good message just
    // vanished. `message` stays normalized for callers that read `err.message`.
    const normalized = new Error(normalizedError);
    normalized.response = error.response;
    normalized.status = error.response?.status;
    normalized.code = error.code;
    return Promise.reject(normalized);
  }
);

export default apiClient;
